import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Box } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';

import { getPropertiesList } from 'entities/Property/model/selectors/PropertySelectors';
import {
    getRouteForbidden,
    getRouteReportsSavingsSummary,
    getRouteReportsSavingsSummaryProduct,
} from 'shared/const/router';
import {
    formatReportCurrency,
    formatReportPercent,
} from 'shared/lib/reportFormatters';
import { ReportDrillInHeader } from 'shared/ui/ReportDrillInHeader';
import { ReportMetric } from 'shared/ui/ReportMetric/ReportMetric';
import { Page } from 'widgets/Page';
import { SavingsSummaryProductsSection } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryProductsSection';
import { SavingsSummaryPropertiesSection } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryPropertiesSection';

import { useReportsPageSavingsSummaryCategory } from '../model/hooks/useReportsPageSavingsSummaryCategory';

import type { ProductMetricsDto } from 'entities/ReportSavingsSummary';

export default () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const properties = useSelector(getPropertiesList) ?? [];
    const {
        isSavingsSummaryVisible,
        categoryId,
        categoryName,
        loading,
        summary,
        products,
        totalSaved,
        totalSavingsPercent,
        orderCount,
        productsCount,
        dateFrom,
        dateTo,
        datePreset,
        lastRequestParamsPresent,
    } = useReportsPageSavingsSummaryCategory();

    const handleBack = useCallback(() => {
        navigate(getRouteReportsSavingsSummary(), {
            state: { fromDrillIn: true },
        });
    }, [navigate]);

    const handleProductRowClick = useCallback(
        (sku: string, product: ProductMetricsDto) => {
            navigate(getRouteReportsSavingsSummaryProduct(sku), {
                state: {
                    product,
                    fromMissedSavings: false,
                },
            });
        },
        [navigate],
    );

    if (isSavingsSummaryVisible === undefined) {
        return null;
    }

    if (!isSavingsSummaryVisible) {
        return <Navigate to={getRouteForbidden()} replace />;
    }

    if (!categoryId || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSavingsSummary()} replace />;
    }

    return (
        <Page
            dataTestId="reports-page-savings-summary-category"
            padding="24px 35px"
        >
            <Box>
                <ReportDrillInHeader
                    title={categoryName}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    datePreset={datePreset}
                    onBack={handleBack}
                    backButtonTitle={t('Savings Summary')}
                    backButtonId="back-to-savings-summary-button"
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        marginBottom: 3,
                    }}
                >
                    <ReportMetric
                        title={t('savingsSummary.totalSavings')}
                        icon={<AttachMoneyIcon fontSize="small" />}
                        primary={formatReportCurrency(totalSaved)}
                        primaryColor="#16a34a"
                        loading={loading}
                        minHeight="100px"
                    />
                    <ReportMetric
                        title={t(
                            'savingsSummary.categoryDrillInTotalSavingsPercent',
                        )}
                        primary={formatReportPercent(totalSavingsPercent)}
                        loading={loading}
                        minHeight="100px"
                    />
                    <ReportMetric
                        title={t('savingsSummary.propertiesColumnOrders')}
                        icon={<ShoppingCartIcon fontSize="small" />}
                        primary={orderCount.toLocaleString()}
                        loading={loading}
                        minHeight="100px"
                    />
                    <ReportMetric
                        title={t('savingsSummary.categoryDrillInProducts')}
                        primary={productsCount.toLocaleString()}
                        loading={loading}
                        minHeight="100px"
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '24px',
                            marginBottom: '40px',
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <SavingsSummaryPropertiesSection
                                byPropertiesData={
                                    summary?.byPropertiesData ?? []
                                }
                                properties={properties}
                                loading={loading}
                                dateFrom={dateFrom}
                                dateTo={dateTo}
                                tableOnly
                                tableColumnIds={[
                                    'property',
                                    'totalSaved',
                                    'orders',
                                ]}
                                showBottomBorder={false}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <SavingsSummaryPropertiesSection
                                byPropertiesData={
                                    summary?.byPropertiesData ?? []
                                }
                                properties={properties}
                                loading={loading}
                                dateFrom={dateFrom}
                                dateTo={dateTo}
                                tableOnly
                                tableColumnIds={[
                                    'property',
                                    'totalSaved',
                                    'orders',
                                ]}
                                showBottomBorder={false}
                            />
                        </Box>
                    </Box>
                    <SavingsSummaryProductsSection
                        products={products}
                        totalSavingsAmount={summary?.savingsAmount}
                        loading={loading}
                        onOpenProductDrillIn={handleProductRowClick}
                        tableOnly
                        tableColumnIds={[
                            'item',
                            'brand',
                            'sku',
                            'properties',
                            'quantity',
                            'avgSupplierPrice',
                            'pricePaid',
                            'totalSaved',
                            'percentSaved',
                        ]}
                        subheader={t(
                            'savingsSummary.productsSubheaderWithinCategory',
                            {
                                categoryName,
                            },
                        )}
                        showBottomBorder={false}
                    />
                </Box>
            </Box>
        </Page>
    );
};
